import React from 'react';
import styled from 'styled-components';
import { Resize } from '../../models';
import { fixArabicText, getMetaValue, device, DocMeta } from '../../helpers';

const PHONE_WIDTH = 255;

const Container = styled.div`
    position: relative;
    margin: 0 14px 40px 14px;
    display: flex;
    flex-direction: column;
    cursor: pointer;

    &:hover {
        div {
            font-weight: 500;
        }
    }

    @media ${device.phone} {
        margin: 0 10px 30px 10px;
    }
`;

const Preview = styled.div<{ width: number }>`
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    width: ${(p) => p.width}px;
    height: ${(p) => (334 * p.width) / 240}px;

    @media ${device.phone} {
        width: ${PHONE_WIDTH}px;
        height: ${(334 * PHONE_WIDTH) / 240}px;
    }
`;

const Cover = styled.img`
    width: 100%;
    border-radius: 2px;
`;

const Info = styled.div<{ width: number }>`
    display: flex;
    flex-direction: column;
    width: ${(p) => p.width}px;

    @media ${device.phone} {
        width: ${PHONE_WIDTH}px;
    }
`;

const Name = styled.div<{ width: number }>`
    text-align: center;
    font-size: ${(p) => (p.width * 0.075).toFixed(2)}px;
    line-height: ${(p) => (p.width * 0.095).toFixed(2)}px;
    font-weight: 500;
    white-space: pre-wrap;

    [lang='ar'] {
        font-size: ${(p) => (p.width * 0.07).toFixed(2)}px;
    }

    @media ${device.phone} {
        font-size: ${(PHONE_WIDTH * 0.065).toFixed(2)}px;
        line-height: ${(PHONE_WIDTH * 0.08).toFixed(2)}px;

        [lang='ar'] {
            font-size: ${(PHONE_WIDTH * 0.07).toFixed(2)}px;
        }
    }
`;

const Authors = styled.div<{ width: number }>`
    color: #5b5f65;
    text-align: center;
    font-weight: 500;
    margin-top: 3px;
    font-size: ${(p) => (p.width * 0.05).toFixed(2)}px;

    @media ${device.phone} {
        font-size: ${(PHONE_WIDTH * 0.05).toFixed(2)}px;
    }
`;

type BookProps = {
    id: number;
    name: string;
    doc_meta: DocMeta[];
    width: number;
    children?: JSX.Element | JSX.Element[];
    onClick: () => void;
};

export function Book({
    id,
    name,
    doc_meta,
    width,
    children,
    onClick,
}: BookProps) {
    const cover = Resize.url('GET', `book/cover/${id}/220`);
    const authors = getMetaValue(doc_meta, 'authors');

    return (
        <Container onClick={onClick}>
            <Preview width={width}>
                <Cover src={cover} />
            </Preview>
            <Info width={width}>
                <Name
                    width={width}
                    dangerouslySetInnerHTML={fixArabicText(name)}
                />
                <Authors width={width}>{authors}</Authors>
            </Info>
            {children}
        </Container>
    );
}

type EBookProps = {
    id: number;
    title: string;
    width: number;
    children?: JSX.Element | JSX.Element[];
    onClick: () => void;
};

export function EBook({ id, title, width, children, onClick }: EBookProps) {
    const cover = Resize.url('GET', `ebook/cover/${id}/220`);

    return (
        <Container onClick={onClick}>
            <Preview width={width}>
                <Cover src={cover} />
            </Preview>
            <Info width={width}>
                <Name
                    width={width}
                    dangerouslySetInnerHTML={fixArabicText(title)}
                />
            </Info>
            {children}
        </Container>
    );
}
